import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import EditNoteIcon from "@mui/icons-material/EditNote";
import FlagIcon from "@mui/icons-material/Flag";
import styles from "./GoalBanner.module.scss";
import type { PageViewSection, SectionStatus } from "./hooks/usePageViewData";

export type GoalBannerProps = {
  hasSummary: boolean;
  isSummaryGenerating: boolean;
  isAllComplete: boolean;
  hasStale: boolean;
  sections: PageViewSection[];
  onUploadClick: () => void;
  onNavigateAppDev: () => void;
};

const STEP_META = [
  { documentType: "system-proposal", label: "システム概要" },
  { documentType: "functional-requirements", label: "主要機能一覧" },
  { documentType: "app-design", label: "作成するアプリ" },
  { documentType: "field-design", label: "作成するフィールド" },
  { documentType: "customize-specification", label: "カスタマイズ（任意）" },
];

export const GoalBanner = ({
  hasSummary,
  isSummaryGenerating,
  isAllComplete,
  hasStale,
  sections,
  onUploadClick,
  onNavigateAppDev,
}: GoalBannerProps) => {
  // 生成中の状態
  if (isSummaryGenerating) {
    return (
      <div className={styles["bannerGenerating"]} data-testid="summary-generating">
        <div className={styles["generatingContent"]}>
          <div className={styles["generatingSpinner"]} />
          <div className={styles["generatingTextGroup"]}>
            <p className={styles["generatingTitle"]}>要件を分析中...</p>
            <p className={styles["generatingSub"]}>
              ドキュメントが生成されるまでしばらくお待ちください
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 未着手: 直接テキスト入力
  if (!hasSummary) {
    return <TextInputBanner onUploadClick={onUploadClick} />;
  }

  // hasSummary=true: インラインバナー
  const isButtonDisabled = !isAllComplete || hasStale;
  const buttonTitle = !isAllComplete
    ? "すべてのドキュメント生成を完了してください"
    : hasStale
      ? "更新が必要なドキュメントがあります"
      : undefined;
  const buttonOnClick = isButtonDisabled ? undefined : onNavigateAppDev;

  const sectionStatusMap = new Map(sections.map((s) => [s.documentType, s.status]));

  const visibleSteps = STEP_META.filter((step) =>
    sections.some((s) => s.documentType === step.documentType),
  );

  const firstStaleLabel = visibleSteps.find(
    (step) => sectionStatusMap.get(step.documentType) === "stale",
  )?.label;

  const currentStepIndex = visibleSteps.findIndex((step) => {
    const status = sectionStatusMap.get(step.documentType);
    return status === "generating" || status === "ready";
  });

  const currentStepMeta = currentStepIndex >= 0 ? visibleSteps[currentStepIndex] : undefined;
  const currentStep = currentStepMeta ?? null;

  const currentStepStatus = sections.find(
    (s) => s.documentType === currentStep?.documentType,
  )?.status;

  const remainingStepCount = visibleSteps.filter((step) => {
    const status = sectionStatusMap.get(step.documentType);
    return status !== "generated" && status !== "stale";
  }).length;

  return (
    <div className={styles["bannerCard"]} data-testid="goal-banner-card">
      <div className={styles["bannerHeaderRow"]}>
        <div className={styles["bannerHeaderLeft"]}>
          <FlagIcon className={styles["bannerFlagIcon"] ?? ""} />
          <span className={styles["bannerHeaderTitle"]}>アプリ生成に必要なドキュメント</span>
        </div>
        <button
          type="button"
          className={styles["addSummaryButton"]}
          onClick={onUploadClick}
          data-testid="add-summary-button"
        >
          <AddIcon className={styles["addSummaryIcon"] ?? ""} />
          議事録を追加
        </button>
      </div>

      <div className={styles["stepperRow"]}>
        {visibleSteps.map((step, index) => {
          const status = sectionStatusMap.get(step.documentType) ?? "locked";
          const stepNumber = index + 1;
          const isLast = index === visibleSteps.length - 1;

          return (
            <StepItem
              key={step.documentType}
              stepNumber={stepNumber}
              label={step.label}
              status={status}
              isLast={isLast}
              nextStatus={
                index < visibleSteps.length - 1
                  ? (sectionStatusMap.get(visibleSteps[index + 1]?.documentType ?? "") ?? "locked")
                  : "locked"
              }
            />
          );
        })}
      </div>

      {currentStep !== null && currentStepStatus === "generating" && (
        <div className={styles["currentStepRow"]}>
          <div className={styles["generatingInfo"]}>
            <span className={styles["generatingBadge"]}>生成中...</span>
            <span className={styles["generatingLabel"]}>{currentStep.label}を生成しています</span>
          </div>
          <button
            type="button"
            className={styles["generateButton"]}
            onClick={buttonOnClick}
            disabled={isButtonDisabled}
            title={buttonTitle}
            data-testid="generate-button"
          >
            アプリを生成する
          </button>
        </div>
      )}

      {currentStep !== null && currentStepStatus === "ready" && (
        <div className={styles["currentStepRow"]}>
          <div className={styles["nextStepRow"]}>
            <div className={styles["nextStepIcon"]}>
              <ArrowForwardIcon className={styles["nextStepIconInner"] ?? ""} />
            </div>
            <div className={styles["nextStepTextGroup"]}>
              <span className={styles["nextStepTitle"]}>次のステップ：{currentStep.label}</span>
              <span className={styles["nextStepSub"]}>
                あと{remainingStepCount}ステップでアプリ生成へ進めます
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles["generateButton"]}
            onClick={buttonOnClick}
            disabled={isButtonDisabled}
            title={buttonTitle}
            data-testid="generate-button"
          >
            アプリを生成する
          </button>
        </div>
      )}

      {isAllComplete && (
        <div className={styles["currentStepRow"]} data-testid="all-complete-row">
          <div className={styles["nextStepRow"]}>
            <div className={styles["nextStepIcon"]}>
              <ArrowForwardIcon className={styles["nextStepIconInner"] ?? ""} />
            </div>
            <div className={styles["nextStepTextGroup"]}>
              <span className={styles["nextStepTitle"]}>
                {hasStale ? `次のステップ：${firstStaleLabel}を更新` : "次のステップ：アプリ生成"}
              </span>
              <span className={styles["nextStepSub"]}>
                {hasStale
                  ? "更新が必要なドキュメントがあります"
                  : "すべてのドキュメントが生成されました"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles["generateButton"]}
            onClick={buttonOnClick}
            disabled={isButtonDisabled}
            title={buttonTitle}
            data-testid="generate-button"
          >
            アプリを生成する
          </button>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------

const TextInputBanner = ({ onUploadClick }: { onUploadClick: () => void }) => (
  <div className={styles["textInputBanner"]}>
    <button type="button" className={styles["textInputBannerContent"]} onClick={onUploadClick}>
      <span className={styles["textInputBannerIcon"]}>
        <EditNoteIcon />
      </span>
      <div className={styles["textInputBannerText"]}>
        <p className={styles["textInputBannerTitle"]}>テキストを貼り付けて要件を入力</p>
        <p className={styles["textInputBannerSub"]}>
          議事録や要件定義のテキストをコピー＆ペーストして生成を開始できます
        </p>
      </div>
    </button>
    <button type="button" className={styles["textInputBannerButton"]} onClick={onUploadClick}>
      直接アップロードして入力する
    </button>
  </div>
);

const StepItem = ({
  stepNumber,
  label,
  status,
  isLast,
  nextStatus,
}: {
  stepNumber: number;
  label: string;
  status: SectionStatus;
  isLast: boolean;
  nextStatus: SectionStatus;
}) => {
  const isCompleted = status === "generated" || status === "stale";
  const isActive = status === "generating" || status === "ready";
  const isLocked = status === "locked";

  const lineCompleted =
    !isLast && (nextStatus === "generated" || nextStatus === "stale" || isCompleted);

  return (
    <>
      <div className={styles["stepFrame"]}>
        <div
          className={`${styles["stepCircle"]} ${
            isCompleted
              ? styles["stepCircleCompleted"]
              : isActive
                ? styles["stepCircleActive"]
                : styles["stepCircleLocked"]
          }`}
        >
          {isCompleted ? (
            <CheckIcon className={styles["stepCheckIcon"] ?? ""} />
          ) : (
            <span
              className={`${styles["stepNumber"]} ${isLocked ? styles["stepNumberLocked"] : ""}`}
            >
              {stepNumber}
            </span>
          )}
        </div>
        <span
          className={`${styles["stepLabel"]} ${
            isCompleted
              ? styles["stepLabelCompleted"]
              : isActive
                ? styles["stepLabelActive"]
                : styles["stepLabelLocked"]
          }`}
        >
          {label}
        </span>
      </div>
      {!isLast && (
        <div
          className={`${styles["stepLine"]} ${lineCompleted ? styles["stepLineCompleted"] : styles["stepLineIncomplete"]}`}
        />
      )}
    </>
  );
};
